import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { defineMessages } from 'react-intl';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import WFS from 'ol/format/WFS';
import { like } from 'ol/format/filter';
import { useMessage } from '@tmapy/intl';
import { actionSetLayerFeatures, getFeaturesExtent, actionSetExtent } from '@tmapy/mapcore';
import { useFetch } from '@tmapy/utils';
import { LAYER_IDS } from '@tmapy/config';
import { SvgSearch, InfoAlert, Form, FormItem, Input, CtaBtn, SIZE } from '@tmapy/style-guide';
import { actionSetTabValue } from '../actions';
import { SearchResults } from './SearchResults';
import './SearchTab.scss';
const msg = defineMessages({
    resultsLimitWarning: {
        id: 'search.resultsLimit.warning',
        defaultMessage: 'Bylo nalezeno více výsledků hledání. Upřesněte prosím zadání.',
    },
});
export const SearchTab = React.memo(({ token, tab, isActive, highlightedFeatureIds, getMapPadding, dispatch }) => {
    const formatMessage = useMessage();
    const tabId = tab.id;
    const handleInputChange = useCallback((e) => {
        const target = e.target;
        dispatch(actionSetTabValue({ tabId, value: target.value }));
    }, [dispatch, tabId]);
    useEffect(() => {
        return () => {
            dispatch(actionSetLayerFeatures(`${LAYER_IDS.SEARCH}-${tabId}`, []));
        };
    }, [dispatch]);
    const value = tab.value ?? '';
    let urlWithoutFilter = `${tab.url}`;
    if (tab.params) {
        for (const [key, value] of Object.entries(tab.params)) {
            if (value !== null && value !== undefined) {
                urlWithoutFilter += `&${key}=${String(value)}`;
            }
        }
    }
    let url = null;
    let method = 'GET';
    let body = null;
    let headers = undefined;
    if ((value ?? '').length >= tab.minQueryLength) {
        switch (tab.type) {
            case 'WFS': {
                if (tab.featureTypes?.length) {
                    // OGC
                    const formatWFS = new WFS();
                    // generate a GetFeature request
                    const featureRequest = formatWFS.writeGetFeature({
                        featureNS: '',
                        featurePrefix: '',
                        featureTypes: tab.featureTypes,
                        outputFormat: 'application/json; charset=utf-8',
                        filter: like(tab.fieldName, encodeURIComponent(`${value}*`)),
                    });
                    url = urlWithoutFilter;
                    body = new XMLSerializer().serializeToString(featureRequest);
                    method = 'POST';
                }
                else {
                    // DEFAULT
                    // TODO: properly escape tab value
                    // TODO: more configurabler query (zbavit se fieldName)
                    url = `${urlWithoutFilter}&cql_filter=${tab.fieldName}${encodeURIComponent(` ilike '${value}%'`)}`;
                }
                break;
            }
            case 'GeoCode': {
                url = `${urlWithoutFilter}&${tab.fieldName}=${encodeURIComponent(value)}`;
                break;
            }
            default: {
                throw new Error('Invalid search type');
            }
        }
    }
    if (tab.isAuth && token) {
        headers = {
            Authorization: `Bearer ${token}`,
        };
    }
    const { queryState, data } = useFetch(url, method, body, headers);
    const limitedData = useMemo(() => {
        if (tab.resultsLimit && data?.features?.length > tab.resultsLimit) {
            return { ...data, features: data.features.slice(0, tab.resultsLimit) };
        }
        return data;
    }, [data, tab.resultsLimit]);
    const handleSubmit = useCallback(() => {
        if (limitedData?.features?.length) {
            const featuresExtent = getFeaturesExtent(limitedData.features);
            const mapPadding = getMapPadding();
            featuresExtent && dispatch(actionSetExtent(featuresExtent, mapPadding));
        }
    }, [dispatch, limitedData]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'sbx-searchTab--form sg-a-px-2 sg-a-pt-2 sg-a-pb-1', children: _jsx(Form, { onSubmit: handleSubmit, children: _jsx(FormItem, { id: `aria-map-search-${tabId}`, hint: formatMessage.fallback([`search.${tabId}.input.hint`]), children: _jsxs("div", { className: 'sg-a-d-f sg-a-ai-b sg-a-g-1', children: [_jsx("div", { className: 'sg-a-f-a sg-a-d-f sg-a-jc-s', children: _jsx(Input, { name: tabId, value: value, placeholder: formatMessage.fallback([`search.${tabId}.input.placeholder`]), onChange: handleInputChange }) }), _jsx(CtaBtn, { type: 'submit', icon: { element: _jsx(SvgSearch, {}) }, size: SIZE.MED })] }) }) }) }), _jsxs("div", { className: 'sg-a-px-2 sg-a-pb-1 sg-u-vs-2', children: [!!tab.resultsLimit && data?.features?.length > tab.resultsLimit && (_jsx(InfoAlert, { children: _jsx(FormattedMessage, { ...msg.resultsLimitWarning }) })), _jsx(SearchResults, { tab: tab, isActive: isActive, highlightedFeatureIds: highlightedFeatureIds, queryState: queryState, data: limitedData, getMapPadding: getMapPadding }, tabId)] })] }));
});
//# sourceMappingURL=SearchTab.js.map